/**
 * Stringplan als Zeichnung (PV-H06).
 *
 * Teil der Anlagendokumentation und im Gespraech ausdruecklich genannt. Was
 * darauf zu sehen sein muss: welches Modul zu welchem String gehoert, in
 * welcher Reihenfolge, und wo Plus und Minus liegen.
 *
 * Gezeichnet wird in der Dachebene, nicht im Luftbild — auf dem Papier soll das
 * Dach unverzerrt liegen, so wie der Monteur davorsteht.
 */

import { huellkasten, type Modulplatz } from '@pv/geometrie';

import { platzSchluessel, type Planung } from '../planung.ts';
import type { Flaeche, Stringzeile } from '../typen.ts';

interface Eigenschaften {
  flaechen: Flaeche[];
  planungen: Map<string, Planung>;
  strings: Stringzeile[];
  onSchliessen: () => void;
}

export function Stringplan({
  flaechen,
  planungen,
  strings,
  onSchliessen,
}: Eigenschaften): React.JSX.Element {
  const offene = strings.filter((s) => !s.geloescht_am);

  return (
    <div
      role="dialog"
      aria-label="Stringplan"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
      }}
      onClick={onSchliessen}
    >
      <div
        style={{
          background: 'var(--weiss)',
          maxWidth: '60rem',
          width: '100%',
          maxHeight: '85vh',
          overflow: 'auto',
          padding: '1.75rem',
          border: '1px solid var(--schwarz)',
        }}
        onClick={(ereignis) => ereignis.stopPropagation()}
      >
        <p className="marke">Anlagendokumentation</p>
        <h2>Stringplan</h2>

        {flaechen.map((flaeche) => {
          const planung = planungen.get(flaeche.id);
          if (!planung || planung.gesetzt.length === 0) return null;
          return (
            <figure key={flaeche.id} style={{ margin: '0 0 2rem' }}>
              <figcaption style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                <strong>{flaeche.name}</strong>
                <span className="gedaempft">
                  {' '}
                  · {planung.zahlen.anzahl} Module · {planung.kennzahlen.azimutGrad.toFixed(0)}° ·{' '}
                  {planung.kennzahlen.neigungGrad}° Neigung
                </span>
              </figcaption>
              <Flaechenplan flaeche={flaeche} planung={planung} strings={offene} />
            </figure>
          );
        })}

        <h3>Strings</h3>
        <ul style={{ fontSize: '0.85rem' }}>
          {offene.map((string) => (
            <li key={string.id}>
              <span
                style={{
                  display: 'inline-block',
                  width: '0.8rem',
                  height: '0.8rem',
                  background: string.farbe,
                  border: '1px solid var(--schwarz)',
                  marginRight: '0.4rem',
                  verticalAlign: 'baseline',
                }}
              />
              {string.name} — {string.module.length} Module
              {string.mppt ? `, MPP-Tracker ${string.mppt}` : ''}
            </li>
          ))}
        </ul>

        <div className="knopfreihe" style={{ marginTop: '1.5rem' }}>
          <button className="wichtig" onClick={() => window.print()}>
            Drucken
          </button>
          <button onClick={onSchliessen}>Schließen</button>
        </div>
      </div>
    </div>
  );
}

/** Eine Flaeche mit ihren Modulen, in der Dachebene. */
function Flaechenplan({
  flaeche,
  planung,
  strings,
}: {
  flaeche: Flaeche;
  planung: Planung;
  strings: Stringzeile[];
}): React.JSX.Element {
  const alle = planung.gesetzt.flatMap((platz) => platz.ecken);
  const kasten = huellkasten(alle);
  const rand = 0.6;
  const breite = kasten.maxX - kasten.minX + 2 * rand;
  const hoehe = kasten.maxY - kasten.minY + 2 * rand;

  const zuString = new Map<string, { farbe: string; nummer: number; name: string }>();
  for (const string of strings) {
    string.module.forEach((modul, i) => {
      if (modul.flaecheId !== flaeche.id) return;
      zuString.set(`${modul.reihe}:${modul.spalte}`, {
        farbe: string.farbe,
        nummer: i + 1,
        name: string.name,
      });
    });
  }

  const linien = strings
    .map((string) => {
      const punkte = string.module
        .filter((modul) => modul.flaecheId === flaeche.id)
        .map((modul) =>
          planung.gesetzt.find((p) => p.reihe === modul.reihe && p.spalte === modul.spalte),
        )
        .filter((p): p is Modulplatz => p !== undefined)
        .map((p) => p.mitte);
      return { farbe: string.farbe, punkte, name: string.name };
    })
    .filter((linie) => linie.punkte.length >= 2);

  return (
    <svg
      viewBox={`${kasten.minX - rand} ${kasten.minY - rand} ${breite} ${hoehe}`}
      style={{ width: '100%', height: 'auto', border: '1px solid var(--linie)' }}
    >
      {/* Die Dachebene wird gespiegelt gezeichnet: v waechst zum First, im SVG
          waechst y nach unten. So liegt der First oben, wie auf dem Papier. */}
      <g transform={`translate(0, ${2 * kasten.minY + hoehe - 2 * rand}) scale(1, -1)`}>
        {planung.gesetzt.map((platz) => {
          const schluessel = platzSchluessel(platz);
          const zugehoerig = zuString.get(schluessel);
          const kante = huellkasten(platz.ecken);
          return (
            <rect
              key={schluessel}
              x={kante.minX}
              y={kante.minY}
              width={kante.maxX - kante.minX}
              height={kante.maxY - kante.minY}
              fill={zugehoerig?.farbe ?? '#ffffff'}
              fillOpacity={zugehoerig ? 0.55 : 1}
              stroke="#000000"
              strokeWidth={0.02}
            />
          );
        })}

        {linien.map((linie, index) => (
          <g key={index}>
            <polyline
              points={linie.punkte.map((p) => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke={linie.farbe}
              strokeWidth={0.06}
              strokeLinejoin="round"
            />
            {/* Plus am Anfang, Minus am Ende — so herum wird angeschlossen. */}
            <circle cx={linie.punkte[0]!.x} cy={linie.punkte[0]!.y} r={0.16} fill={linie.farbe} />
            <circle
              cx={linie.punkte[linie.punkte.length - 1]!.x}
              cy={linie.punkte[linie.punkte.length - 1]!.y}
              r={0.16}
              fill="#ffffff"
              stroke={linie.farbe}
              strokeWidth={0.05}
            />
          </g>
        ))}
      </g>

      {/* Beschriftung ausserhalb der Spiegelung, sonst stuende sie auf dem Kopf. */}
      {linien.map((linie, index) => {
        const anfang = linie.punkte[0]!;
        const ende = linie.punkte[linie.punkte.length - 1]!;
        const gespiegelt = (y: number): number => 2 * kasten.minY + hoehe - 2 * rand - y;
        return (
          // Ueber den Anschlusspunkt, nicht daneben: Auf gleicher Hoehe lief die
          // Stringlinie mitten durch die Schrift, und "+ String 1" war nicht zu
          // lesen. Der weisse Rand haelt sie auch ueber den Modulen lesbar —
          // paintOrder sorgt dafuer, dass er hinter der Fuellung liegt.
          <g
            key={index}
            fontSize={0.3}
            fontWeight={600}
            fontFamily="inherit"
            stroke="#ffffff"
            strokeWidth={0.14}
            paintOrder="stroke"
            strokeLinejoin="round"
          >
            <text x={anfang.x} y={gespiegelt(anfang.y) - 0.3} fill={linie.farbe} textAnchor="middle">
              + {linie.name}
            </text>
            <text x={ende.x} y={gespiegelt(ende.y) - 0.3} fill={linie.farbe} textAnchor="middle">
              −
            </text>
          </g>
        );
      })}
    </svg>
  );
}
